import { ExpandMore } from '@mui/icons-material';
import { Button, CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { t } from 'i18next';
import { Fragment, useId, useMemo, useState } from 'react';
import CsvIcon from '../../../assets/images/export-csv-icon.svg';
import JiraIcon from '../../../assets/images/export-jira-icon.svg';
import PrintIcon from '../../../assets/images/export-print-icon.svg';
import ServiceNowIcon from '../../../assets/images/service-now-icon.svg';
import { JiraIssue } from '../../../integrations/jira/models/valueObjects/JiraIssue';
import { Instance } from '../../../reports/issues/issue/instance/models/Instance';
import { CsvExportManager } from '../../services/CsvExportManager';
import { PrintExportManager } from '../../services/PrintExportManager';

type Props = {
  instance: Instance;
  includeFilteredItems: boolean;
  onJiraExport?: () => Promise<JiraIssue | null>;
  onServiceNowExport?: () => Promise<void>;
  onError: (e: unknown) => void;
};

enum ExportFormat {
  PRINT = 'print',
  CSV = 'csv',
  JIRA = 'jira',
  SERVICE_NOW = 'service_now',
}

const ICON_PROPS = { width: 24, height: 24, 'aria-hidden': true };

export const ExportButton = ({ instance, includeFilteredItems, onJiraExport, onServiceNowExport, onError }: Props) => {
  const buttonId = useId();
  const menuId = useId();

  const [printManager] = useState(new PrintExportManager());
  const [csvManager] = useState(new CsvExportManager());

  const [pending, setPending] = useState(false);
  const [pendingType, setPendingType] = useState<ExportFormat>();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onExportClick = async (format: ExportFormat) => {
    try {
      setPending(true);
      setPendingType(format);

      switch (format) {
        case ExportFormat.CSV:
          await csvManager.exportInstance(instance.getData(), {
            reportId: instance.getReportId(),
            issueNumber: instance.getRuleNumber(),
            instanceId: instance.getId(),
            includeFilteredItems,
          });
          break;
        case ExportFormat.PRINT:
          await printManager.exportInstance(instance.getData(), {
            reportId: instance.getReportId(),
            issueNumber: instance.getRuleNumber(),
            instanceId: instance.getId(),
            includeFilteredItems,
          });
          break;
        case ExportFormat.JIRA:
          if (onJiraExport) {
            await onJiraExport();
          }
          break;
        case ExportFormat.SERVICE_NOW:
          if (onServiceNowExport) {
            await onServiceNowExport();
          }
          break;
      }

      setPending(false);
      setPendingType(undefined);
      handleClose();
    } catch (e: unknown) {
      setPending(false);
      setPendingType(undefined);
      onError(e);
    }
  };

  const optionalIntegrations = useMemo(() => {
    const items = [];
    if (onJiraExport) {
      items.push({
        format: ExportFormat.JIRA,
        icon: <JiraIcon {...ICON_PROPS} />,
        label: 'jira',
      });
    }

    if (onServiceNowExport) {
      items.push({
        format: ExportFormat.SERVICE_NOW,
        icon: <ServiceNowIcon {...ICON_PROPS} />,
        label: 'serviceNow',
      });
    }

    return items;
  }, [onJiraExport, onServiceNowExport]);

  return (
    <Fragment>
      <Button
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant="outlined"
        disabled={pending}
        endIcon={<ExpandMore />}
      >
        {t('export')}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': buttonId }}
        slotProps={{ paper: { sx: { minWidth: '12rem' } } }}
      >
        <MenuItem disabled={pending} onClick={() => onExportClick(ExportFormat.PRINT)} sx={{ alignItems: 'center' }} divider>
          <ListItemIcon>{pendingType === ExportFormat.PRINT ? <CircularProgress size={20} /> : <PrintIcon {...ICON_PROPS} />}</ListItemIcon>
          <ListItemText sx={{ pl: 1 }}>
            <Typography>{t('print')}</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem disabled={pending} onClick={() => onExportClick(ExportFormat.CSV)} sx={{ alignItems: 'center' }} divider={optionalIntegrations.length > 0}>
          <ListItemIcon>{pendingType === ExportFormat.CSV ? <CircularProgress size={20} /> : <CsvIcon {...ICON_PROPS} />}</ListItemIcon>
          <ListItemText sx={{ pl: 1 }}>
            <Typography>{t('csv')}</Typography>
          </ListItemText>
        </MenuItem>
        {optionalIntegrations.map((item, index) => (
          <MenuItem
            key={item.format}
            disabled={pending}
            onClick={() => onExportClick(item.format)}
            sx={{ alignItems: 'center' }}
            divider={index < optionalIntegrations.length - 1}
          >
            <ListItemIcon>{pendingType === item.format ? <CircularProgress size={20} /> : item.icon}</ListItemIcon>
            <ListItemText sx={{ pl: 1 }}>
              <Typography>{t(item.label)}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Fragment>
  );
};
